import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useGPS } from '../../contexts/GPSContext';
import { Modal } from '../ui/Modal';

const DEFAULT_COLORS = [
  '#5b3df0', '#7c5cff', '#2f6df0', '#36a7d6', '#2bbfae', '#9b8cf5', '#b6bcc8', '#c2c7d2', '#dfe3ea'
];

const GPSStatusDistributionChart = ({
  className = '',
  title,
  icon,
  filterFn,
  groupByFn,
  dateField = 'created_at',
  colors = DEFAULT_COLORS,
  barColor = '#375dfb',
  accent = { bg: '#f3f1fe', color: '#5b3df0' },
  emptyMessage = 'Aucune donnée disponible.'
}) => {
  const gps = useGPS();
  const [viewMode, setViewMode] = useState('pie');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredIssues = useMemo(() => {
    // Général tab: sense-scoped dataset with drafts excluded (summarySensedIssues),
    // falling back to the draft-excluded full set.
    const baseIssues = Array.isArray(gps?.summarySensedIssues)
      ? gps.summarySensedIssues
      : (Array.isArray(gps?.summaryIssues) ? gps.summaryIssues : []);
    return filterFn ? baseIssues.filter(filterFn) : baseIssues;
  }, [gps?.summarySensedIssues, gps?.summaryIssues, filterFn]);

  const data = useMemo(() => {
    if (filteredIssues.length === 0) return [];

    const map = new Map();
    filteredIssues.forEach(issue => {
      const key = groupByFn(issue);
      // Skip null, undefined, or empty string keys
      if (key !== null && key !== undefined && key !== '') {
        map.set(key, (map.get(key) || 0) + 1);
      }
    });

    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredIssues, groupByFn]);

  const { evolutionData, allStatuses } = useMemo(() => {
    if (filteredIssues.length === 0 || !gps?.startDate || !gps?.endDate) {
      return { evolutionData: [], allStatuses: [] };
    }

    // Determine granularity based on date range
    const start = new Date(gps.startDate);
    const end = new Date(gps.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let mode = 'monthly';
    if (diffDays <= 31) mode = 'daily';
    else if (diffDays <= 120) mode = 'weekly';

    // Get all unique statuses (sorted by count to match pie chart order)
    const statusMap = new Map();
    filteredIssues.forEach(issue => {
      const status = groupByFn(issue);
      // Skip null, undefined, or empty string statuses
      if (status !== null && status !== undefined && status !== '') {
        statusMap.set(status, (statusMap.get(status) || 0) + 1);
      }
    });
    const allStatuses = Array.from(statusMap.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([status]) => status);

    // Initialize buckets for all periods to ensure continuity
    const buckets = new Map();
    const formatDateKey = (date) => {
      if (mode === 'daily') {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      } else if (mode === 'weekly') {
        const tempDate = new Date(date.getTime());
        tempDate.setHours(0, 0, 0, 0);
        tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
        const week1 = new Date(tempDate.getFullYear(), 0, 4);
        const weekNum = 1 + Math.round(((tempDate - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
        return `${tempDate.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
      } else {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
    };

    // Initialize all buckets in the date range
    const cursor = new Date(start);
    while (cursor <= end) {
      const key = formatDateKey(cursor);
      if (!buckets.has(key)) {
        const bucket = {};
        allStatuses.forEach(status => {
          bucket[status] = 0;
        });
        buckets.set(key, bucket);
      }

      if (mode === 'daily') {
        cursor.setDate(cursor.getDate() + 1);
      } else if (mode === 'weekly') {
        cursor.setDate(cursor.getDate() + 7);
      } else {
        cursor.setMonth(cursor.getMonth() + 1);
      }
    }

    // Count issues by date and status
    filteredIssues.forEach(issue => {
      const dateValue = issue?.[dateField] || issue?.createdAt || issue?.created_at;
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return;

      const key = formatDateKey(date);
      const status = groupByFn(issue);

      // Skip null, undefined, or empty string statuses
      if (status !== null && status !== undefined && status !== '' && buckets.has(key)) {
        buckets.get(key)[status] = (buckets.get(key)[status] || 0) + 1;
      }
    });

    // Convert to array and format labels
    const evolutionData = Array.from(buckets.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, statusCounts]) => {
        let label;
        if (mode === 'daily') {
          label = new Date(key).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
        } else if (mode === 'weekly') {
          const week = key.split('-W')[1];
          label = `S${week}`;
        } else {
          label = new Date(key + '-01').toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
        }
        return {
          month: label,
          ...statusCounts
        };
      });

    return { evolutionData, allStatuses };
  }, [filteredIssues, dateField, groupByFn, gps?.startDate, gps?.endDate]);

  if (!data.length) {
    return (
      <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm min-h-[350px] flex items-center justify-center">
        <p className="text-gray-600 dark:text-gray-400 text-center">{emptyMessage}</p>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const top = data[0];
  const topPct = total > 0 ? Math.round((top.value / total) * 100) : 0;

  return (
    <div className={`bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm h-full flex flex-col ${className}`}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl flex-none" style={{ background: accent.bg, color: accent.color }}>
            {icon && React.cloneElement(icon, { className: 'w-[18px] h-[18px]', style: { color: 'inherit' } })}
          </span>
          <span className="text-base font-bold text-gray-900 dark:text-white">
            {title}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('pie')}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${viewMode === 'pie' ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
            >
              Répartition
            </button>
            <button
              onClick={() => setViewMode('evolution')}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${viewMode === 'evolution' ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
            >
              Évolution
            </button>
          </div>
          {viewMode === 'evolution' && evolutionData.length > 0 && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center justify-center rounded-md border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 size-7 sm:size-8"
              title="Agrandir"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4 sm:size-[18px] text-gray-600 dark:text-gray-300">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-[280px] pb-4 flex flex-col">
        {viewMode === 'pie' ? (
          <div className="flex items-center gap-5 sm:gap-7">
            <div className="relative flex-none" style={{ width: 168, height: 168 }}>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-extrabold leading-none text-gray-900 dark:text-white">{topPct}%</span>
                <span className="mt-1 text-[11px] font-semibold text-gray-400 dark:text-gray-500 max-w-[92px] truncate text-center">{top.name}</span>
              </div>
              <ResponsiveContainer width={168} height={168}>
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={82}
                    innerRadius={58}
                    paddingAngle={1.2}
                    startAngle={90}
                    endAngle={-270}
                    label={false}
                    stroke="none"
                  >
                    {data.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    wrapperStyle={{ zIndex: 30 }}
                    formatter={(value, name) => {
                      const percent = ((value / total) * 100).toFixed(1);
                      return [`${value} (${percent}%)`, name];
                    }}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-2.5 text-xs">
              {data.map((item, index) => {
                const pct = ((item.value / total) * 100).toFixed(1).replace('.', ',');
                return (
                  <span key={`lg-${index}`} className="flex items-center gap-2.5 font-semibold text-gray-600 dark:text-gray-300">
                    <span className="w-2.5 h-2.5 rounded-[3px] flex-none" style={{ background: colors[index % colors.length] }} />
                    <span className="truncate">{item.name}</span>
                    <span className="ml-auto pl-2 text-gray-900 dark:text-white whitespace-nowrap">{pct} %</span>
                  </span>
                );
              })}
            </div>
          </div>
        ) : evolutionData.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="text-xs text-gray-400 dark:text-gray-500">
              Pas de données pour afficher une évolution
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={evolutionData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={true} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9CA3AF" axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
                formatter={(value) => [`${value} tickets`]}
              />
              {allStatuses.map((status, index) => (
                <Bar
                  key={status}
                  dataKey={status}
                  fill={colors[index % colors.length]}
                  radius={[4, 4, 0, 0]}
                  name={status}
                />
              ))}
              <Legend />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`${title} - Évolution`}
        maxWidth="max-w-[1200px]"
        style={{ maxWidth: '1200px' }}
      >
        <div className="h-[min(600px,calc(80vh-120px))]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={evolutionData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={true} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9CA3AF" axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
                formatter={(value) => [`${value} tickets`]}
              />
              {allStatuses.map((status, index) => (
                <Bar
                  key={status}
                  dataKey={status}
                  fill={colors[index % colors.length]}
                  radius={[4, 4, 0, 0]}
                  name={status}
                />
              ))}
              <Legend />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Modal>
    </div>
  );
};

export default GPSStatusDistributionChart;
